import { useMemo } from "react";

export const DOTS = "...";

const range = (start: number, end: number) => {
  let length = end - start + 1;
  return Array.from({ length }, (_, idx) => idx + start);
};

type usePaginationProps = {
  page: number;
  pageSize: number;
  totalCount: number;
  siblingCount?: number;
};

export const usePagination = ({
  page,
  pageSize,
  totalCount,
  siblingCount = 1,
}: usePaginationProps) => {
  const paginationRange = useMemo(() => {
    // actual total number of pages
    const totalPageCount = Math.ceil(totalCount / pageSize) - 1;

    // max page number buttons we want displayed at once in UI
    // we might have 100 pages, but only want to display: [1, 2, 3, ... 100] (4 visible + dots)
    const totalPageNumbers = siblingCount + 3;

    // Calc left & right sibling page buttons index
    const leftSiblingIndex = Math.max(page - siblingCount, 1);
    const rightSiblingIndex = Math.min(page + siblingCount, totalPageCount);

    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;

    const firstPageIndex = 0;
    const lasPageIndex = totalPageCount;

    // Display no dots
    if (totalPageNumbers >= totalPageCount) {
      return range(0, totalPageCount);
    }

    // Only display right dots
    if (!shouldShowLeftDots && shouldShowRightDots) {
      let leftItemCount = 2 + 2 * siblingCount;
      let leftRange = range(0, leftItemCount);

      return [...leftRange, DOTS, totalPageCount];
    }

    // Only display left dots
    if (shouldShowLeftDots && !shouldShowRightDots) {
      let rightItemCount = 3 + 2 * siblingCount;
      let rightRange = range(
        totalPageCount - rightItemCount + 1,
        totalPageCount
      );

      return [firstPageIndex, DOTS, ...rightRange];
    }

    // Display both left and right dots
    if (shouldShowLeftDots && shouldShowRightDots) {
      let middleRange = range(leftSiblingIndex, rightSiblingIndex);
      return [firstPageIndex, DOTS, ...middleRange, DOTS, lasPageIndex];
    }
  }, [page, pageSize, totalCount, siblingCount]);

  return paginationRange;
};
